/**
 * Cash-X API Service
 * Handles all communication with Cash-X Backend for delivery management
 * 
 * UNIFIED LOGIN: Drivers now login with Email + Password via Cash-X Backend
 * Uses backend proxy to bypass CORS restrictions
 */

import axios from 'axios';

// Use Flotto Backend Proxy for Cash-X API calls (to bypass CORS)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const CASHX_PROXY_BASE = `${BACKEND_URL}/api/cashx`;

// Direct Cash-X URL (using environment variable for deployment flexibility)
const CASHX_DIRECT_URL = process.env.REACT_APP_BACKEND_URL || '';
const CASHX_TEAM_API = `${CASHX_DIRECT_URL}/api/team`;

class CashXService {
  constructor() {
    this.token = null;
    this.driverId = null;
    this.driverName = null;
    this.driverEmail = null;
    this.tenantId = null;
  }

  /**
   * Get stored Cash-X token from localStorage
   */
  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('cashx_driver_token');
    }
    return this.token;
  }

  /**
   * Store Cash-X token
   */
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('cashx_driver_token', token);
    } else {
      localStorage.removeItem('cashx_driver_token');
    }
  }

  /**
   * Get driver ID
   */
  getDriverId() {
    if (!this.driverId) {
      this.driverId = localStorage.getItem('driver_id');
    }
    return this.driverId;
  }

  /**
   * Get driver name
   */
  getDriverName() {
    if (!this.driverName) {
      this.driverName = localStorage.getItem('driver_name');
    }
    return this.driverName;
  }

  /**
   * Get driver email
   */
  getDriverEmail() {
    if (!this.driverEmail) {
      this.driverEmail = localStorage.getItem('driver_email');
    }
    return this.driverEmail;
  }

  /**
   * Get tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      this.tenantId = localStorage.getItem('tenant_id');
    }
    return this.tenantId;
  }

  /**
   * Store driver info after login
   */
  setDriverInfo(driverId, driverName, driverEmail, tenantId) {
    this.driverId = driverId;
    this.driverName = driverName;
    this.driverEmail = driverEmail;
    this.tenantId = tenantId;

    if (driverId) {
      localStorage.setItem('driver_id', driverId);
    } else {
      localStorage.removeItem('driver_id');
    }
    if (driverName) {
      localStorage.setItem('driver_name', driverName);
    } else {
      localStorage.removeItem('driver_name');
    }
    if (driverEmail) {
      localStorage.setItem('driver_email', driverEmail);
    } else {
      localStorage.removeItem('driver_email');
    }
    if (tenantId) {
      localStorage.setItem('tenant_id', tenantId);
    } else {
      localStorage.removeItem('tenant_id');
    }
  }

  /**
   * Login to Cash-X Backend with Email + Password
   * NEW UNIFIED LOGIN ENDPOINT
   * Uses backend proxy to avoid CORS issues
   * 
   * @param {string} email - Driver email address
   * @param {string} password - Driver password
   */
  async login(email, password) {
    try {
      // Use proxy endpoint for login to avoid CORS
      const response = await fetch(
        `${CASHX_PROXY_BASE}/team/driver-login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and driver info
        this.setToken(data.token);
        this.setDriverInfo(
          data.driver_id,
          data.driver_name,
          data.email,
          data.tenant_id
        );

        return {
          success: true,
          driver: {
            id: data.driver_id,
            name: data.driver_name,
            email: data.email,
            tenant_id: data.tenant_id
          },
          token: data.token,
          message: data.message || 'Login erfolgreich'
        };
      }

      return { 
        success: false, 
        error: data.detail || 'Ungültige E-Mail oder Passwort' 
      };
    } catch (error) {
      console.error('Cash-X Login Error:', error);
      return {
        success: false,
        error: error.message || 'Verbindungsfehler zum Server'
      };
    }
  }

  /**
   * Logout from Cash-X - Clear all stored data
   */
  async logout() {
    try {
      // Clear local storage
      this.setToken(null);
      this.setDriverInfo(null, null, null, null);
      
      // Clear legacy storage keys
      localStorage.removeItem('cashx_driver_id');
      localStorage.removeItem('cashx_driver_token');
      
      // Clear sync data
      localStorage.removeItem('restaurant_name');
      localStorage.removeItem('logo_url');
      localStorage.removeItem('logo_base64');
      localStorage.removeItem('primary_color');
      localStorage.removeItem('secondary_color');
      localStorage.removeItem('restaurant_location');
      localStorage.removeItem('restaurant_settings');
      
      // Reset CSS variables
      document.documentElement.style.removeProperty('--cashx-primary');
      document.documentElement.style.removeProperty('--cashx-secondary');
    } catch (error) {
      console.error('Cash-X Logout Error:', error);
    }
  }

  /**
   * Sync all restaurant data from Cash-X
   * Call this after successful login to get branding, location, shift, orders
   */
  async syncData() {
    try {
      const token = this.getToken();
      if (!token) {
        return { success: false, error: 'No token available' };
      }

      // Use proxy for team API (avoid CORS)
      const response = await fetch(
        `${CASHX_PROXY_BASE}/team/sync-to-flotto`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return { 
          success: false, 
          error: errorData.detail || 'Sync failed' 
        };
      }

      const data = await response.json();

      // Store restaurant data
      if (data.restaurant) {
        localStorage.setItem('restaurant_name', data.restaurant.name || '');
        localStorage.setItem('restaurant_tenant_id', data.restaurant.tenant_id || '');
      }

      // Store branding
      if (data.branding) {
        if (data.branding.logo_url) {
          // Build full logo URL
          const logoUrl = data.branding.logo_url.startsWith('http') 
            ? data.branding.logo_url 
            : `${CASHX_DIRECT_URL}${data.branding.logo_url}`;
          localStorage.setItem('logo_url', logoUrl);
        }
        if (data.branding.logo_base64) {
          localStorage.setItem('logo_base64', data.branding.logo_base64);
        }
        if (data.branding.primary_color) {
          localStorage.setItem('primary_color', data.branding.primary_color);
          document.documentElement.style.setProperty('--cashx-primary', data.branding.primary_color);
        }
        if (data.branding.secondary_color) {
          localStorage.setItem('secondary_color', data.branding.secondary_color);
          document.documentElement.style.setProperty('--cashx-secondary', data.branding.secondary_color);
        }
      }

      // Store location
      if (data.location) {
        localStorage.setItem('restaurant_location', JSON.stringify(data.location));
      }

      // Store settings
      if (data.settings) {
        localStorage.setItem('restaurant_settings', JSON.stringify(data.settings));
      }

      return {
        success: true,
        data: data
      };

    } catch (error) {
      console.error('Cash-X Sync Error:', error);
      return {
        success: false,
        error: error.message || 'Sync failed'
      };
    }
  }

  /**
   * Get stored branding data
   */
  getBranding() {
    return {
      restaurantName: localStorage.getItem('restaurant_name'),
      logoUrl: localStorage.getItem('logo_url'),
      logoBase64: localStorage.getItem('logo_base64'),
      primaryColor: localStorage.getItem('primary_color'),
      secondaryColor: localStorage.getItem('secondary_color')
    };
  }

  /**
   * Get stored restaurant location
   */
  getRestaurantLocation() {
    const location = localStorage.getItem('restaurant_location');
    return location ? JSON.parse(location) : null;
  }

  /**
   * Get stored restaurant settings
   */
  getRestaurantSettings() {
    const settings = localStorage.getItem('restaurant_settings');
    return settings ? JSON.parse(settings) : null;
  }

  /**
   * Apply stored branding to document
   */
  applyBranding() {
    const branding = this.getBranding();
    if (branding.primaryColor) {
      document.documentElement.style.setProperty('--cashx-primary', branding.primaryColor);
    }
    if (branding.secondaryColor) {
      document.documentElement.style.setProperty('--cashx-secondary', branding.secondaryColor);
    }
  }

  /**
   * Get authorization headers
   */
  getAuthHeaders() {
    const token = this.getToken();
    if (!token) {
      throw new Error('No Cash-X token available. Please login.');
    }
    return {
      Authorization: `Bearer ${token}`
    };
  }

  /**
   * Decode JWT token to get payload
   * @param {string} token - JWT token
   */
  decodeJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('JWT Decode Error:', error);
      return null;
    }
  }

  /**
   * Claim an order by scanning QR code
   * @param {string} qrToken - JWT token from QR code
   */
  async claimOrder(qrToken) {
    try {
      // Decode QR token to get order_id
      const payload = this.decodeJWT(qrToken);
      if (!payload || !payload.order_id) {
        return { success: false, error: 'Invalid QR code' };
      }

      const orderId = payload.order_id;

      // Call Cash-X API to claim order
      const response = await axios.post(
        `${CASHX_PROXY_BASE + "/driver"}/orders/${orderId}/claim`,
        { qr_token: qrToken },
        { headers: this.getAuthHeaders() }
      );

      return {
        success: true,
        order: response.data.order,
        message: response.data.message || 'Order claimed successfully'
      };
    } catch (error) {
      console.error('Claim Order Error:', error);
      
      // Handle 401 - token expired
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired. Please login again.', requiresAuth: true };
      }

      // Handle 409 - already claimed
      if (error.response?.status === 409) {
        return { success: false, error: 'Order already claimed by another driver' };
      }

      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to claim order'
      };
    }
  }

  /**
   * Get my active orders
   */
  async getMyOrders() {
    try {
      const response = await axios.get(`${CASHX_PROXY_BASE + "/driver"}/orders/my`, {
        headers: this.getAuthHeaders()
      });

      return {
        success: true,
        orders: response.data.orders || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Get My Orders Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load orders'
      };
    }
  }

  /**
   * Update order status
   * @param {string} orderId - Order ID
   * @param {string} status - New status ('delivered' or 'problem')
   * @param {string} problemReason - Reason if status is 'problem'
   */
  async updateOrderStatus(orderId, status, problemReason = null) {
    try {
      const body = { status };
      if (status === 'problem' && problemReason) {
        body.problem_reason = problemReason;
      }

      const response = await axios.post(
        `${CASHX_PROXY_BASE + "/driver"}/orders/${orderId}/status`,
        body,
        { headers: this.getAuthHeaders() }
      );

      return {
        success: true,
        order: response.data.order,
        message: response.data.message || 'Status updated'
      };
    } catch (error) {
      console.error('Update Order Status Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update status'
      };
    }
  }

  /**
   * Check if driver is authenticated with Cash-X
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Get restaurant GPS location
   */
  async getRestaurantLocation() {
    try {
      const response = await axios.get(`${CASHX_PROXY_BASE + "/driver"}/shift/restaurant-location`, {
        headers: this.getAuthHeaders()
      });

      return {
        success: true,
        location: {
          lat: response.data.lat,
          lng: response.data.lng,
          radius: response.data.radius_meters,
          address: response.data.address
        }
      };
    } catch (error) {
      console.error('Get Restaurant Location Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get restaurant location'
      };
    }
  }

  /**
   * Start shift with QR code and GPS verification
   * @param {string} qrToken - QR token from shift QR code
   * @param {number} gpsLat - Current GPS latitude
   * @param {number} gpsLng - Current GPS longitude
   */
  async startShift(qrToken, gpsLat, gpsLng) {
    try {
      const response = await axios.post(
        `${CASHX_PROXY_BASE + "/driver"}/shift/start`,
        {
          qr_token: qrToken,
          gps_lat: gpsLat,
          gps_lng: gpsLng
        },
        { headers: this.getAuthHeaders() }
      );

      return {
        success: true,
        shift: {
          shift_id: response.data.shift_id,
          started_at: response.data.started_at
        },
        message: response.data.message || 'Schicht gestartet'
      };
    } catch (error) {
      console.error('Start Shift Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      // Handle specific shift errors
      if (error.response?.status === 400) {
        return {
          success: false,
          error: error.response.data.detail || 'GPS zu weit vom Restaurant entfernt'
        };
      }

      if (error.response?.status === 409) {
        return {
          success: false,
          error: 'Schicht bereits aktiv'
        };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Fehler beim Starten der Schicht'
      };
    }
  }

  /**
   * End current shift with GPS verification
   * @param {number} gpsLat - Current GPS latitude
   * @param {number} gpsLng - Current GPS longitude
   */
  async endShift(gpsLat, gpsLng) {
    try {
      const response = await axios.post(
        `${CASHX_PROXY_BASE + "/driver"}/shift/end`,
        {
          gps_lat: gpsLat,
          gps_lng: gpsLng
        },
        { headers: this.getAuthHeaders() }
      );

      return {
        success: true,
        shift: {
          shift_id: response.data.shift_id,
          ended_at: response.data.ended_at,
          duration_minutes: response.data.duration_minutes,
          deliveries_count: response.data.deliveries_count
        },
        message: response.data.message || 'Schicht beendet'
      };
    } catch (error) {
      console.error('End Shift Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      if (error.response?.status === 400) {
        return {
          success: false,
          error: error.response.data.detail || 'GPS zu weit vom Restaurant entfernt'
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          error: 'Keine aktive Schicht gefunden'
        };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Fehler beim Beenden der Schicht'
      };
    }
  }

  /**
   * Get current shift status
   */
  async getCurrentShift() {
    try {
      const response = await axios.get(`${CASHX_PROXY_BASE + "/driver"}/shift/current`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.active) {
        return {
          success: true,
          active: true,
          shift: {
            shift_id: response.data.shift_id,
            started_at: response.data.started_at,
            duration_minutes: response.data.duration_minutes,
            deliveries_count: response.data.deliveries_count || 0
          }
        };
      }

      return {
        success: true,
        active: false,
        message: response.data.message
      };
    } catch (error) {
      console.error('Get Current Shift Error:', error);
      
      if (error.response?.status === 401) {
        this.setToken(null);
        return { success: false, error: 'Session expired', requiresAuth: true };
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get shift status'
      };
    }
  }
}

// Export singleton instance
const cashxService = new CashXService();
export default cashxService;
